import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RobotIcon, PaperPlaneTiltIcon, SparkleIcon } from "@phosphor-icons/react";
import { sortByUnit } from "@/lib/unitSort";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Summarize this truck's repair history.",
  "Predict the next maintenance items based on history and mileage.",
  "What recurring issues do you see across the fleet?",
  "Identify trucks with abnormally high repair costs.",
  "What should I prioritize this week?",
];

export default function AIAssistant() {
  const [params] = useSearchParams();
  const [trucks, setTrucks] = useState([]);
  const [truckId, setTruckId] = useState(params.get("truck_id") || "");
  const [question, setQuestion] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/trucks");
      setTrucks(data);
    })();
  }, []);

  const ask = async (q) => {
    const text = q || question;
    if (!text.trim()) return;
    setLoading(true);
    setHistory((h) => [...h, { role: "user", text, ts: Date.now() }]);
    setQuestion("");
    try {
      const { data } = await api.post("/ai/ask", { truck_id: truckId || null, question: text });
      setHistory((h) => [...h, { role: "ai", text: data.answer, ts: Date.now() }]);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "AI request failed";
      setHistory((h) => [...h, { role: "ai", text: `ERROR: ${msg}`, ts: Date.now() }]);
      toast.error("AI request failed");
    } finally {
      setLoading(false);
    }
  };

  const selected_truck = trucks.find((t) => t.id === truckId);

  return (
    <div data-testid="ai-page">
      <PageHeader eyebrow="Rep Fleet AI" title="AI Maintenance Assistant" subtitle="Ask anything about repair history, predict next services, and spot trends.">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500 font-bold">Scope</span>
          <Select value={truckId || "fleet"} onValueChange={(v) => setTruckId(v === "fleet" ? "" : v)}>
            <SelectTrigger className="w-72 rounded-sm" data-testid="ai-scope-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fleet">Entire fleet</SelectItem>
              {sortByUnit(trucks).map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.unit_number} · {t.make} {t.model}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </PageHeader>

      <div className="p-8 max-w-4xl space-y-6">
        <div className="bg-white border border-slate-300 rounded-sm p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-blue-600 rounded-sm flex items-center justify-center">
              <RobotIcon size={20} weight="bold" className="text-white" />
            </div>
            <div>
              <div className="font-display font-bold text-lg tracking-tight">Rep Fleet AI</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Powered by Claude Sonnet 4.5</div>
            </div>
          </div>

          {selected_truck && (
            <div className="mb-3 text-xs font-mono text-slate-600 bg-slate-50 border border-slate-200 rounded-sm px-3 py-2">
              CONTEXT: Unit {selected_truck.unit_number} · {selected_truck.year} {selected_truck.make} {selected_truck.model} · {Number(selected_truck.mileage).toLocaleString()} mi
            </div>
          )}

          {history.length === 0 && (
            <div className="space-y-2 mb-4">
              <div className="text-xs font-mono uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1">
                <SparkleIcon size={12} weight="fill" /> Try one of these:
              </div>
              <div className="flex flex-wrap gap-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => ask(s)}
                    disabled={loading}
                    data-testid={`ai-suggest-${s.slice(0, 15)}`}
                    className="text-xs text-left border border-slate-300 hover:border-blue-500 hover:bg-blue-50 px-3 py-2 rounded-sm transition-colors duration-150"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {history.length > 0 && (
            <div className="terminal mb-3 scrollbar-thin" data-testid="ai-history">
              {history.map((m, i) => (
                <div key={`${m.role}-${i}-${m.ts || ""}`} className="mb-3">
                  {m.role === "user" ? (
                    <>
                      <span className="prompt">{">"} </span>
                      <span className="user-msg">{m.text}</span>
                    </>
                  ) : (
                    <>
                      <span className="prompt">FLEETIQ@ {"->"} </span>
                      <span className="ai-msg">{m.text}</span>
                    </>
                  )}
                </div>
              ))}
              {loading && <div className="text-slate-400">FLEETIQ@ thinking...</div>}
            </div>
          )}

          <form
            onSubmit={(e) => { e.preventDefault(); ask(); }}
            className="flex gap-2"
            data-testid="ai-form"
          >
            <Textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Ask about repair patterns, predict next service, or spot cost outliers..."
              className="flex-1 rounded-sm min-h-[60px] resize-none"
              data-testid="ai-question-input"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  ask();
                }
              }}
            />
            <Button
              type="submit"
              disabled={loading || !question.trim()}
              className="bg-slate-900 hover:bg-slate-800 rounded-sm self-end h-11 px-5"
              data-testid="ai-submit"
            >
              <PaperPlaneTiltIcon size={16} weight="bold" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
